<template>
    <div class="card">
        <div class="card-content">
            <div class="media" v-if="!multipleItems">
                <div class="media-left">
                    <figure class="image is-48x48">
                        <img :src="acquisitions.items[0].iconUrl" alt="Item icon">
                    </figure>
                </div>
                <div class="media-content">
                    <p class="title is-4">{{ acquisitions.acquisitionCount }} {{ acquisitions.items[0].name }}</p>
                    <p class="subtitle is-6">
                        <currency-amount v-bind:key="index"
                                         v-for="(resourceAmount, index) in acquisitions.inventoryDifference.filter(x => x.resource.type === 'currency' && x.amount > 0)"
                                         :amount="resourceAmount.amount"
                                         :currency="resourceAmount.resource.name"
                        />
                    </p>
                </div>
            </div>
            <div class="media" v-else>
                <div class="media-left">
                    <figure class="image is-48x48">
                        <b-icon icon="table-large" size="is-large"/>
                    </figure>
                </div>
                <div class="media-content">
                    <p class="title is-4">{{ acquisitions.acquisitionCount }} acquisitions of {{
                        acquisitions.items.length }} items</p>
                    <p class="subtitle is-6">
                        <currency-amount v-bind:key="index"
                                         v-for="(resourceAmount, index) in acquisitions.inventoryDifference"
                                         :amount="resourceAmount.amount"
                                         :currency="resourceAmount.resource.name"
                        />
                    </p>
                </div>
            </div>
            <b-notification v-for="warning in warnings" v-bind:key="warning" type="is-warning" has-icon>{{ warning }}</b-notification>
            <b-tabs>
                <b-tab-item label="Summary">
                    <div class="columns">
                        <div class="column">
                            <p class="title is-5">Summary</p>
                            <table class="table is-narrow is-striped">
                                <tr v-bind:key="'sell' + index"
                                    v-for="(prices, index) in acquisitions.soldMaterialPrices"
                                >
                                    <!-- TODO: Mention sale type -->
                                    <th>
                                        Sales
                                    </th>
                                    <td>
                                        <currency-amount :currency="prices.resource.name"
                                                         :amount="prices.amount"
                                        />
                                    </td>
                                </tr>
                                <tr v-bind:key="'buy' + index"
                                    v-for="(prices, index) in acquisitions.boughtMaterialPrices"
                                >
                                    <th>Bought materials
                                        <template v-if="prices.source === 'tp-buy-instant'">
                                            (<b-icon icon="scale-balance" size="is-small" title="Trading Post"/> instant)
                                        </template>
                                        <template v-else-if="prices.source === 'tp-buy-order'">
                                            (<b-icon icon="scale-balance" size="is-small" title="Trading Post"/> order)
                                        </template>
                                        <template v-else-if="prices.source === 'merchant-buy'">
                                            (merchant)
                                        </template>
                                        <template v-else>
                                            (unknown source)
                                        </template>
                                    </th>
                                    <td>
                                        <currency-amount :currency="prices.resource.name"
                                                         :amount="-prices.amount"
                                        />
                                    </td>
                                </tr>
                                <tr v-bind:key="'profit' + index"
                                    v-for="(resourceAmount, index) in acquisitions.inventoryDifference.filter(x => x.amount >= 0 && x.resource.type === 'currency')"
                                >
                                    <th>Profit (after taxes)</th>
                                    <td>
                                        <currency-amount :currency="resourceAmount.resource.name"
                                                         :amount="resourceAmount.amount"
                                        />
                                    </td>
                                </tr>
                            </table>
                        </div>
                        <div class="column">
                            <p class="title is-5">Resulting inventory</p>
                            <b-table :data="acquisitions.inventoryDifference.filter(x => x.amount > 0)"
                                     :default-sort="['resource.name', 'desc']"
                                     :striped="true"
                                     :narrowed="true">
                                <template slot-scope="props">
                                    <b-table-column field="icon" :width="40" label="">
                                        <ResourceImage class="is-24x24" :resource="props.row.resource" />
                                    </b-table-column>
                                    <b-table-column field="resource.name" label="Item" sortable>
                                        {{ props.row.resource.name }}
                                    </b-table-column>
                                    <b-table-column field="amount" label="Count" sortable>
                                        {{ props.row.amount }}
                                    </b-table-column>
                                </template>
                            </b-table>
                        </div>
                    </div>
                </b-tab-item>
                <b-tab-item label="Bought materials">
                    <p class="title is-5">Bought materials</p>
                    <b-table :data="acquisitions.boughtMaterials"
                             :default-sort="['totalPrice', 'desc']"
                             :striped="true"
                             :narrowed="true">
                        <template slot-scope="props">
                            <b-table-column field="icon" :width="40" label="">
                                <ResourceImage class="is-24x24" :resource="props.row.resource" />
                            </b-table-column>
                            <b-table-column field="resource.name" label="Item" sortable>
                                {{ props.row.resource.name }}
                            </b-table-column>
                            <b-table-column field="amount" label="Count" sortable>
                                {{ props.row.amount }}
                            </b-table-column>
                            <b-table-column field='totalPrice' :custom-sort="sortPrice" label="Cost" sortable>
                                <currency-amount v-bind:key="index"
                                                 v-for="(resourceAmount, index) in props.row.totalPrice"
                                                 :amount="resourceAmount.amount"
                                                 :currency="resourceAmount.resource.name"
                                />
                            </b-table-column>
                            <b-table-column field="source" label="Source" sortable>
                                <template v-if="props.row.source === 'tp-buy-instant'">
                                    <b-icon icon="scale-balance" size="is-small" title="Trading Post"/> Trading Post - instant buy
                                </template>
                                <template v-else-if="props.row.source === 'tp-buy-order'">
                                    <b-icon icon="scale-balance" size="is-small" title="Trading Post"/> Trading Post - buy order
                                </template>
                                <template v-else-if="props.row.source === 'merchant-buy'">
                                    Merchant purchase
                                </template>
                                <template v-else>
                                    Unknown source
                                </template>
                            </b-table-column>
                        </template>
                    </b-table>
                </b-tab-item>
                <b-tab-item label="Crafting process">
                    <div class="content">
                        <ul>
                            <li :key="index" v-for="(action, index) in acquisitions.actionSequence">
                                <AcquisitionAction :action="action" />
                            </li>
                        </ul>
                        <!--
                        <br>
                        <b-message type="is-info" has-icon>
                            TODO: Selling process
                            <br>
                            TODO: Crafting discipline icons
                        </b-message>
                        -->
                    </div>
                </b-tab-item>
                <b-tab-item label="Sold stacks" v-if="acquisitions.soldMaterials.length > 0">
                    <b-table :data="acquisitions.soldMaterials"
                             striped
                             narrowed
                             detailed
                    >
                        <template slot-scope="props">
                            <b-table-column field="icon" label="">
                                <ResourceImage class="is-24x24" :resource="props.row.resource" />
                            </b-table-column>
                            <b-table-column field="item.name" label="Sold item">
                                {{ props.row.resource.name }}
                            </b-table-column>
                            <b-table-column field="amount" label="Count">
                                {{ props.row.amount }}
                            </b-table-column>
                            <!--
                            <b-table-column field="sellUnitPrice" label="Unit price (pre-tax)">
                            </b-table-column>
                            -->
                            <b-table-column field="averageProfit" label="Average profit per item">
                                <currency-amount v-bind:key="index"
                                                 v-for="(resourceAmount, index) in props.row.averageProfit"
                                                 :amount="Math.round(resourceAmount.amount * 10) / 10"
                                                 :currency="resourceAmount.resource.name"
                                />
                            </b-table-column>
                            <b-table-column field="profit" label="Profit (post-tax)">
                                <currency-amount v-bind:key="index"
                                                 v-for="(resourceAmount, index) in props.row.inventoryDiff"
                                                 :amount="resourceAmount.amount"
                                                 :currency="resourceAmount.resource.name"
                                />
                            </b-table-column>
                        </template>
                        <template slot="detail" slot-scope="detail">
                            <b-tabs>
                                <b-tab-item label="Sold stacks">
                                    <b-table :data="detail.row.soldStacks"
                                             striped
                                             narrowed
                                    >
                                        <template slot-scope="props">
                                            <b-table-column label="" :width="40">
                                                <ResourceImage class="is-24x24" :resource="detail.row.resource"/>
                                            </b-table-column>
                                            <b-table-column field="resource.name" label="Sold item" sortable>
                                                {{ detail.row.resource.name }}
                                            </b-table-column>
                                            <b-table-column field="amount" label="Count" sortable>
                                                {{props.row.amount}}
                                            </b-table-column>
                                            <b-table-column field="unitPrice" label="Unit price (pre-tax)">
                                                <currency-amount v-bind:key="index"
                                                                 v-for="(resourceAmount, index) in props.row.unitPrice"
                                                                 :amount="resourceAmount.amount"
                                                                 :currency="resourceAmount.resource.name"
                                                />
                                            </b-table-column>
                                            <b-table-column field="totalPriceAfterTax" label="Total price (post-tax)">
                                                <currency-amount v-bind:key="index"
                                                                 v-for="(resourceAmount, index) in props.row.totalPriceAfterTax"
                                                                 :amount="resourceAmount.amount"
                                                                 :currency="resourceAmount.resource.name"
                                                />
                                            </b-table-column>
                                        </template>
                                    </b-table>
                                </b-tab-item>
                                <b-tab-item label="Bought materials">
                                    <b-table :data="detail.row.boughtMaterials"
                                             striped
                                             narrowed
                                    >
                                        <template slot-scope="props">
                                            <b-table-column field="icon" :width="40" label="">
                                                <ResourceImage class="is-24x24" :resource="props.row.resource" />
                                            </b-table-column>
                                            <b-table-column field="resource" label="Bought item" sortable>
                                                {{ props.row.resource.name }}
                                            </b-table-column>
                                            <b-table-column field="amount" label="Count" sortable>
                                                {{props.row.amount}}
                                            </b-table-column>
                                            <b-table-column field="totalPrice" label="Total price" sortable>
                                                <currency-amount v-bind:key="index"
                                                                 v-for="(resourceAmount, index) in props.row.totalPrice"
                                                                 :amount="resourceAmount.amount"
                                                                 :currency="resourceAmount.resource.name"
                                                />
                                            </b-table-column>
                                            <b-table-column field="source" label="Source" sortable>
                                                {{ props.row.source }}
                                            </b-table-column>
                                        </template>
                                    </b-table>
                                </b-tab-item>
                                <b-tab-item label="Inventory changes">
                                    <b-table :data="detail.row.inventoryDiff"
                                             striped
                                             narrowed
                                    >
                                        <template slot-scope="props">
                                            <b-table-column field="icon" :width="40" label="">
                                                <ResourceImage class="is-24x24" :resource="props.row.resource"/>
                                            </b-table-column>
                                            <b-table-column field="item.name" label="Item name" sortable>
                                                {{ props.row.resource.name !== "Coppers" ? props.row.resource.name : "Gold" }}
                                            </b-table-column>
                                            <b-table-column field="amount" label="Inventory amount" sortable>
                                                <template v-if="props.row.resource.name === 'Coppers'">
                                                    <gold-amount :amount="props.row.amount"/>
                                                </template>
                                                <template v-else>
                                                    {{props.row.amount}}
                                                </template>
                                            </b-table-column>
                                        </template>
                                    </b-table>
                                </b-tab-item>
                            </b-tabs>
                        </template>
                    </b-table>
                </b-tab-item>
            </b-tabs>
        </div>
    </div>
</template>

<script>
    import _ from 'lodash';
    import AcquisitionAction from "@/components/AcquisitionAction";
    import CurrencyAmount from "@/components/CurrencyAmount";
    import ResourceImage from "@/components/ResourceImage";
    import GoldAmount from "@/components/GoldAmount";

    export default {
        props: ['acquisition'],
        name: "AcquisitionCard",
        components: {
            GoldAmount,
            CurrencyAmount,
            AcquisitionAction,
            ResourceImage
        },
        data() {
            return {
                warnings: []
            }
        },
        computed: {
            acquisitions() {
                let cloned = _.cloneDeep(this.acquisition);
                cloned.boughtMaterialPrices = [];
                cloned.boughtMaterials.forEach(material => {
                    material.totalPrice.forEach(price => {
                        let prices = cloned.boughtMaterialPrices.find(x => {
                            return _.isEqual(x.resource, price.resource) && _.isEqual(x.source, material.source)
                        });
                        if (prices) {
                            prices.amount += price.amount;
                        } else {
                            cloned.boughtMaterialPrices.push({
                                resource: price.resource,
                                source: material.source,
                                amount: price.amount
                            });
                        }
                    });
                });
                cloned.soldMaterialPrices = [];
                cloned.soldMaterials.forEach(sale => {
                    sale.soldStacks.forEach(soldStack => {
                        soldStack.totalPriceAfterTax.forEach(price => {
                            let prices = cloned.soldMaterialPrices.find(x => {
                                return _.isEqual(x.resource, price.resource) && _.isEqual(x.type, soldStack.type)
                            });
                            if (prices) {
                                prices.amount += price.amount;
                            } else {
                                cloned.soldMaterialPrices.push({
                                    resource: price.resource,
                                    type: soldStack.type,
                                    amount: price.amount
                                });
                            }
                        });
                    });
                });

                cloned.soldMaterials.forEach(sale => {
                    sale.amount = _.sumBy(sale.soldStacks, x => x.amount);
                    sale.averageProfit = _.cloneDeep(sale.inventoryDiff);
                    sale.averageProfit.forEach(resourceAmount => {
                        resourceAmount.amount /= sale.amount;
                    });

                    /*
                    sale.soldStacks.forEach(stack => {
                        stack.averageUnitPriceAfterTax = _.cloneDeep(stack.totalPriceAfterTax);
                        stack.averageUnitPriceAfterTax.forEach(resourceAmount => {
                            resourceAmount.amount /= stack.amount;
                        })

                    });
                    */
                });

                return cloned;
            },
            multipleItems() {
                return this.acquisitions.items.length !== 1;
            },
            sellsIncluded() {
                return this.acquisitions.soldMaterials.length > 0;
            },
        },
        methods: {
            sortPrice(material1, material2, isAsc) {
                let result = 0;
                let profit1 = material1.totalPrice;
                let profit2 = material2.totalPrice;
                let coppers1 = _.find(profit1, x => x.resource.name === 'Coppers');
                let coppers2 = _.find(profit2, x => x.resource.name === 'Coppers');
                if (coppers1 && coppers2) {
                    result = coppers1.amount - coppers2.amount;
                } else if (coppers1) {
                    result = 1;
                } else if (coppers2) {
                    result = -1;
                } else {
                    result = _.sumBy(profit1, x => x.amount) - _.sumBy(profit2, x => x.amount);
                }
                return isAsc ? result : -result;
            }
        }
    }
</script>

<style scoped>
</style>